import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';

// ---------------------------------------------------------------------------
// Sidebar config. Edit this array to change the navigation.
//   to:    route to open
//   soon:  true renders an inert row (no dead links)
//   group: expandable section (like Kaggle's "Data Hub" / "More")
// ---------------------------------------------------------------------------
const NAV_ITEMS = [
  { label: 'Home', icon: 'Home', to: '/user-dashboard' },
  { label: 'Courses', icon: 'BookOpen', to: '/course-catalog' },
  { label: 'Learning Paths', icon: 'Route', soon: true },
  { label: 'Live Classes', icon: 'Radio', soon: true },
  { label: 'Mentorship', icon: 'HeartHandshake', soon: true },
  { label: 'Credentials', icon: 'Award', soon: true },
  {
    label: 'My Learning',
    icon: 'GraduationCap',
    children: [
      { label: 'My Courses', soon: true },
      { label: 'My Paths', soon: true },
      { label: 'Achievements', soon: true },
    ],
  },
  {
    label: 'More',
    icon: 'ChevronsDown',
    children: [
      { label: 'Blog', soon: true },
      { label: 'Help Center', soon: true },
    ],
  },
];

const SEARCH_ROUTE = '/search'; // search results page; query goes in ?q=
const LOGO_URL = 'https://pub-b365ff8e20e448c3a7257b35173d9a38.r2.dev/NESOL_2D_Logo_-_Black_and_White-removebg-preview.png';

const isActive = (pathname, to) => !!to && (pathname === to || pathname.startsWith(`${to}/`));

// Labels hide only on desktop when collapsed, so the mobile drawer is always full width.
const hideWhenCollapsed = (collapsed) => (collapsed ? 'lg:hidden' : '');

const NavRow = ({ item, collapsed, active, onNavigate, indent = false }) => {
  const base = [
    'flex items-center h-11 rounded-xl text-sm transition-colors',
    indent ? 'pl-[52px] pr-3' : 'gap-4 px-3',
    collapsed && !indent ? 'lg:justify-center lg:px-0 lg:gap-0' : '',
  ].join(' ');

  const content = (
    <>
      {!indent && <Icon name={item.icon} size={22} className="flex-shrink-0" />}
      <span className={`truncate ${hideWhenCollapsed(collapsed)}`}>{item.label}</span>
    </>
  );

  if (!item.to || item.soon) {
    return (
      <div className={`${base} text-foreground cursor-default`} title="Coming soon" aria-disabled="true">
        {content}
      </div>
    );
  }

  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={`${base} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
        active ? 'bg-muted font-medium text-foreground' : 'text-foreground hover:bg-muted'
      }`}
    >
      {content}
    </Link>
  );
};

const NavGroup = ({ group, collapsed, onExpandSidebar }) => {
  const [open, setOpen] = useState(!!group.defaultOpen);

  const handleClick = () => {
    // On the collapsed desktop rail a group has no room to open, so widen the sidebar first.
    if (collapsed && window.innerWidth >= 1024) {
      onExpandSidebar();
      setOpen(true);
      return;
    }
    setOpen((o) => !o);
  };

  return (
    <div>
      <button
        type="button"
        onClick={handleClick}
        aria-expanded={open}
        className={`w-full flex items-center gap-4 h-11 px-3 rounded-xl text-sm text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
          collapsed ? 'lg:justify-center lg:px-0 lg:gap-0' : ''
        }`}
      >
        <Icon name={group.icon} size={22} className="flex-shrink-0" />
        <span className={`truncate ${hideWhenCollapsed(collapsed)}`}>{group.label}</span>
        <Icon
          name="ChevronDown"
          size={16}
          className={`ml-auto transition-transform ${open ? 'rotate-180' : ''} ${hideWhenCollapsed(collapsed)}`}
        />
      </button>
      {open && (
        <div className={`mt-0.5 space-y-0.5 ${hideWhenCollapsed(collapsed)}`}>
          {group.children.map((child) => (
            <NavRow key={child.label} item={child} collapsed={collapsed} indent active={false} />
          ))}
        </div>
      )}
    </div>
  );
};

// Section header used across the Kaggle-style home feed:
// icon + title on the left, "See all" on the right, one-line description below.
export const SectionHeader = ({ icon, title, description, actionLabel = 'See all', actionTo, onAction }) => (
  <div className="mb-4">
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        {icon && <Icon name={icon} size={20} className="text-foreground flex-shrink-0" />}
        <h2 className="text-lg sm:text-xl font-semibold text-foreground truncate">{title}</h2>
      </div>
      {actionTo ? (
        <Link to={actionTo} className="text-sm font-medium text-foreground hover:underline flex-shrink-0">
          {actionLabel}
        </Link>
      ) : onAction ? (
        <button type="button" onClick={onAction} className="text-sm font-medium text-foreground hover:underline flex-shrink-0">
          {actionLabel}
        </button>
      ) : null}
    </div>
    {description && <p className="mt-1.5 text-sm text-muted-foreground">{description}</p>}
  </div>
);

// ---------------------------------------------------------------------------
// Account panel: slides in from the right like Kaggle's.
//   header (avatar, name, Nesolearn ID, close)
//   menu list
//   "Your notifications" feed
//
// notifications: [{ id, icon, title, description, to?, onClick? }]
// ---------------------------------------------------------------------------
const PANEL_ROW =
  'flex items-center gap-4 w-full h-12 px-6 text-sm text-foreground text-left transition-colors focus-visible:outline-none focus-visible:bg-muted';

const PanelRow = ({ item, onClose }) => {
  const content = (
    <>
      <Icon name={item.icon} size={22} className="flex-shrink-0" />
      <span className="truncate">{item.label}</span>
    </>
  );

  if (item.action) {
    return (
      <button type="button" role="menuitem" onClick={item.action} className={`${PANEL_ROW} hover:bg-muted`}>
        {content}
      </button>
    );
  }
  if (item.href) {
    return (
      <a href={item.href} role="menuitem" onClick={onClose} className={`${PANEL_ROW} hover:bg-muted`}>
        {content}
      </a>
    );
  }
  if (item.to && !item.soon) {
    return (
      <Link to={item.to} role="menuitem" onClick={onClose} className={`${PANEL_ROW} hover:bg-muted`}>
        {content}
      </Link>
    );
  }
  return (
    <div role="menuitem" aria-disabled="true" title="Coming soon" className={`${PANEL_ROW} cursor-default`}>
      {content}
    </div>
  );
};

const AccountPanel = ({ open, onClose, user, onSignOut, notifications, readIds, onRead, onReadAll, closeRef }) => {
  const navigate = useNavigate();
  const unread = notifications.filter((n) => !readIds.includes(n.id)).length;

  const items = [
    { label: 'Your Learning', icon: 'BookOpen', to: '/user-dashboard' },
    {
      label: 'Your Profile',
      icon: 'User',
      to: user?.nesolearnId ? `/id/${user.nesolearnId}` : null,
      soon: !user?.nesolearnId,
    },
    { label: 'Your Credentials', icon: 'Award', soon: true },
    { label: 'Settings', icon: 'Settings', soon: true },
    { label: 'Help & Support', icon: 'Mail', href: 'mailto:info@nesolearn.com' },
    onSignOut && { label: 'Sign Out', icon: 'LogOut', action: onSignOut },
  ].filter(Boolean);

  const openNotification = (n) => {
    onRead(n.id);
    onClose();
    if (n.onClick) n.onClick();
    if (n.to) navigate(n.to);
  };

  return (
    <>
      {/* Dimmed page behind the panel */}
      <div
        className={`fixed inset-0 z-[60] bg-black/40 transition-opacity duration-200 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Account"
        aria-hidden={!open}
        className={`fixed inset-y-0 right-0 z-[70] w-[380px] max-w-full flex flex-col bg-white dark:bg-background border-l border-border shadow-xl transition-[transform,visibility] duration-200 ${
          open ? 'translate-x-0 visible' : 'translate-x-full invisible'
        }`}
      >
        {/* Header: avatar, name, Nesolearn ID, close */}
        <div className="flex items-center gap-3 px-5 py-3 min-h-[64px] border-b border-border flex-shrink-0">
          <span className="w-9 h-9 rounded-full overflow-hidden bg-muted flex-shrink-0">
            {user?.avatar && <img src={user.avatar} alt="" className="w-full h-full object-cover" />}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-base font-medium text-foreground truncate">{user?.name}</p>
            {user?.nesolearnId && <p className="text-xs text-muted-foreground truncate">{user.nesolearnId}</p>}
          </div>
          <button
            type="button"
            ref={closeRef}
            onClick={onClose}
            aria-label="Close account panel"
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <Icon name="X" size={22} />
          </button>
        </div>

        {/* Menu */}
        <nav role="menu" aria-label="Account" className="py-2 flex-shrink-0">
          {items.map((item) => (
            <PanelRow key={item.label} item={item} onClose={onClose} />
          ))}
        </nav>

        {/* Notifications */}
        <div className="flex items-center justify-between px-6 h-14 border-t border-b border-border flex-shrink-0">
          <div className="flex items-center gap-4">
            <Icon name="Bell" size={22} />
            <h2 className="text-base font-medium text-foreground">Your notifications</h2>
          </div>
          {unread > 0 && (
            <button type="button" onClick={onReadAll} className="text-xs font-medium text-foreground hover:underline">
              Mark all as read
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <span className="mx-auto mb-3 w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <Icon name="Bell" size={18} />
              </span>
              <p className="text-sm font-medium text-foreground">You're all caught up</p>
              <p className="mt-1 text-xs text-muted-foreground">New activity will show up here.</p>
            </div>
          ) : (
            notifications.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => openNotification(n)}
                className="w-full flex items-start gap-4 px-6 py-4 text-left hover:bg-muted transition-colors focus-visible:outline-none focus-visible:bg-muted"
              >
                <span className="w-10 h-10 flex-shrink-0 rounded-full border-[1.5px] border-border flex items-center justify-center">
                  <Icon name={n.icon || 'Bell'} size={18} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-foreground">{n.title}</span>
                  <span className="block mt-0.5 text-xs text-muted-foreground">{n.description}</span>
                </span>
                {!readIds.includes(n.id) && (
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" aria-label="Unread" />
                )}
              </button>
            ))
          )}
        </div>
      </aside>
    </>
  );
};

const NO_NOTIFICATIONS = [];

const DashboardShell = ({ user, onSignOut, notifications = NO_NOTIFICATIONS, children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(false); // desktop: icon-only rail
  const [mobileOpen, setMobileOpen] = useState(false); // mobile: slide-in drawer
  const [panelOpen, setPanelOpen] = useState(false); // account panel
  const [readIds, setReadIds] = useState([]);
  const [query, setQuery] = useState('');
  const avatarBtnRef = useRef(null);
  const closeBtnRef = useRef(null);

  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem('nesolearn-dashboard-theme') === 'dark' ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  });

  // Puts the `dark` class on <html> so Tailwind's dark: variants, your existing dark
  // theme variables and portaled modals all follow it. It is removed when the
  // dashboard unmounts, so the public site is never affected.
  useLayoutEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    try {
      localStorage.setItem('nesolearn-dashboard-theme', theme);
    } catch {}
    return () => document.documentElement.classList.remove('dark');
  }, [theme]);

  const closePanel = useCallback(() => {
    setPanelOpen(false);
    if (avatarBtnRef.current) avatarBtnRef.current.focus();
  }, []);

  const markRead = useCallback((id) => {
    setReadIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const markAllRead = useCallback(() => {
    setReadIds(notifications.map((n) => n.id));
  }, [notifications]);

  const unreadCount = notifications.filter((n) => !readIds.includes(n.id)).length;

  // Close the drawer and account panel on navigation.
  useEffect(() => {
    setMobileOpen(false);
    setPanelOpen(false);
  }, [pathname]);

  // While the account panel is open: focus its close button, close on Escape,
  // and stop the page behind it from scrolling.
  useEffect(() => {
    if (!panelOpen) return undefined;
    if (closeBtnRef.current) closeBtnRef.current.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') closePanel();
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [panelOpen, closePanel]);

  const toggleSidebar = () => {
    if (window.innerWidth >= 1024) setCollapsed((c) => !c);
    else setMobileOpen((o) => !o);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (q) navigate(`${SEARCH_ROUTE}?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className="dash-shell min-h-screen bg-white dark:bg-background text-foreground" data-theme={theme}>
      <style>{`
        .dash-shell[data-theme="light"] .dash-main .border.border-border { border-color: transparent; }
        .dash-shell[data-theme="light"] .dash-main .bg-card { background-color: #ffffff; }
        .dash-shell[data-theme="light"] .dash-main [class*="shadow"]:not([class*="ring"]) { box-shadow: none; }
      `}</style>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setMobileOpen(false)} aria-hidden="true" />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex flex-col w-64 bg-white dark:bg-background border-r border-border transition-[transform,width] duration-200 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 ${collapsed ? 'lg:w-[72px]' : 'lg:w-64'}`}
        aria-label="Primary"
      >
        <div className="flex items-center gap-2 h-16 px-3 flex-shrink-0">
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label="Toggle sidebar"
            className="w-11 h-11 flex items-center justify-center rounded-full hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <Icon name="Menu" size={22} />
          </button>
          <Link
            to="/user-dashboard"
            className={`flex items-center gap-2 ${hideWhenCollapsed(collapsed)}`}
            aria-label="Nesolearn home"
          >
            <img src={LOGO_URL} alt="" className="h-8 w-8 object-contain flex-shrink-0 dark:invert" />
            <span className="text-xl font-bold tracking-tight">Nesolearn</span>
          </Link>
        </div>

        <div className="px-3 pb-3 flex-shrink-0">
          <Link
            to="/course-catalog"
            className={`flex items-center gap-2 h-12 rounded-full bg-accent text-white text-sm font-semibold shadow-sm hover:bg-accent/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 ${
              collapsed ? 'lg:w-12 lg:justify-center lg:px-0 px-5' : 'px-5'
            }`}
          >
            <Icon name="Plus" size={20} className="flex-shrink-0" />
            <span className={hideWhenCollapsed(collapsed)}>Start learning</span>
          </Link>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 space-y-0.5">
          {NAV_ITEMS.map((item) =>
            item.children ? (
              <NavGroup key={item.label} group={item} collapsed={collapsed} onExpandSidebar={() => setCollapsed(false)} />
            ) : (
              <NavRow
                key={item.label}
                item={item}
                collapsed={collapsed}
                active={isActive(pathname, item.to)}
                onNavigate={() => setMobileOpen(false)}
              />
            )
          )}
        </nav>

        <div className={`border-t border-border px-3 py-3 flex-shrink-0 flex items-center gap-1 ${collapsed ? 'lg:flex-col' : ''}`}>
          <div className="flex-1 min-w-0">
            <NavRow
              item={{ label: 'Earn NESOL', icon: 'Gift', to: '/earn' }}
              collapsed={collapsed}
              active={isActive(pathname, '/earn')}
            />
          </div>
          <button
            type="button"
            onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            title={theme === 'dark' ? 'Light theme' : 'Dark theme'}
            className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <Icon name={theme === 'dark' ? 'Sun' : 'Moon'} size={20} />
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className={`transition-[padding] duration-200 ${collapsed ? 'lg:pl-[72px]' : 'lg:pl-64'}`}>
        {/* Top bar: wide search on the left, avatar on the right */}
        <header className="sticky top-0 z-30 bg-white/95 dark:bg-background/95 backdrop-blur">
          <div className="flex items-center gap-3 h-16 px-4 sm:px-6 lg:px-8">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
            >
              <Icon name="Menu" size={22} />
            </button>

            <form onSubmit={handleSearch} className="relative flex-1 max-w-3xl" role="search">
              <Icon name="Search" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground pointer-events-none" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search"
                aria-label="Search courses"
                className="w-full h-11 pl-12 pr-4 rounded-full border border-border bg-white dark:bg-card text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/40"
              />
            </form>

            <div className="relative ml-auto flex items-center">
              {unreadCount > 0 && (
                <span
                  className="absolute -left-3 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-blue-500"
                  aria-hidden="true"
                />
              )}
              <button
                type="button"
                ref={avatarBtnRef}
                onClick={() => setPanelOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={panelOpen}
                aria-label={unreadCount > 0 ? `Account, ${unreadCount} unread notifications` : 'Account'}
                className="w-10 h-10 rounded-full overflow-hidden border border-border bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                {user?.avatar ? (
                  <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="w-full h-full flex items-center justify-center">
                    <Icon name="User" size={18} />
                  </span>
                )}
              </button>
            </div>
          </div>
        </header>

        <main className="dash-main px-4 sm:px-6 lg:px-8 pb-12">
          <div className="mx-auto w-full max-w-6xl">{children}</div>
        </main>
      </div>

      <AccountPanel
        open={panelOpen}
        onClose={closePanel}
        user={user}
        onSignOut={onSignOut}
        notifications={notifications}
        readIds={readIds}
        onRead={markRead}
        onReadAll={markAllRead}
        closeRef={closeBtnRef}
      />
    </div>
  );
};

export default DashboardShell;